/**
 * YazioApp - Router-based Nutrition Tracking
 * Layout and navigation for Yazio nutrition integration with React Router
 */

import { RefreshCw } from "lucide-react";
import React from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { YazioLogo } from "@/components/icons/aspects";
import { Button } from "@/components/ui/Button";

/**
 * WHAT:
 *   Main application component for Yazio nutrition tracking with router-based navigation.
 *
 * WHY:
 *   Provides layout and navigation for Yazio nutrition data management with tab-based interface.
 *
 * PROPS:
 *   None (root component using React Router)
 *
 * BEHAVIOR:
 *   - Manages tab navigation (Dashboard, Goals, Settings)
 *   - Provides navigation between different Yazio views
 *   - Displays loading states during data fetch
 *   - Handles route changes and active tab highlighting
 *   - Integrates with React Router for navigation
 *
 * RISKS:
 *   - NOTE: Tab configuration is hardcoded - may not match actual routes
 *   - NOTE: Active tab detection uses pathname matching - may fail with nested routes
 *   - WARN: Router navigation may cause data loss if not saved before navigation
 *
 * @returns {JSX.Element} Yazio application component with navigation and routing
 *
 */
export const YazioApp: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Tab configuration
  const tabs = [
    {
      id: "dashboard",
      label: "Dashboard",
      path: "/nexus/health/yazio/dashboard",
    },
    { id: "goals", label: "Goals", path: "/nexus/health/yazio/goals" },
    { id: "meals", label: "Meals", path: "/nexus/health/yazio/meals" },
    { id: "sync", label: "Sync", path: "/nexus/health/yazio/sync" },
  ];

  const currentTab = tabs.find((tab) => location.pathname === tab.path) ?? tabs[0];

  const handleTabClick = (path: string) => {
    const result = navigate(path);
    if (result instanceof Promise) {
      result.catch(() => {
        // Reason: Navigation errors are handled silently to prevent UI disruption
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800">
      {/* Header */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="w-full max-w-none px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <YazioLogo size="app" />
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-slate-800" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Yazio Nutrition</h1>
                <div className="flex items-center space-x-4 text-sm text-slate-400">
                  <span className="flex items-center space-x-1">
                    <span className="w-2 h-2 bg-orange-500 rounded-full" />
                    <span>Connected</span>
                  </span>
                  <span>•</span>
                  <span className="flex items-center space-x-1">
                    <span className="w-2 h-2 bg-green-500 rounded-full" />
                    <span>Last Sync: Today</span>
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Button
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
                onClick={() => {
                  // Note: Disconnect implementation pending
                }}
                variant="outline"
              >
                Disconnect
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="w-full max-w-none px-6">
        <nav className="border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex space-x-8">
              {tabs.map((tab) => (
                <button
                  className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                    currentTab?.id === tab.id
                      ? "border-orange-500 text-orange-400"
                      : "border-transparent text-slate-400 hover:text-slate-300 hover:border-slate-600"
                  }`}
                  key={tab.id}
                  onClick={() => {
                    handleTabClick(tab.path);
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <Button
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
              onClick={() => {
                // Note: Sync implementation pending
              }}
              size="sm"
              variant="outline"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Sync
            </Button>
          </div>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="w-full max-w-none px-6 py-6">
        <div className="space-y-6">
          <Outlet />
        </div>
      </div>
    </div>
  );
};
